import React, {useState} from 'react';

import {Button, FilePicker, Modal} from '@/components';
import {
  isPdfFile,
  isWithinLiveAccessMaxSize,
} from '@/screens/Merchants/LiveAccess/liveAccessStatus';
import {showErrorMessage} from '@/utils/showErrorMessage';

type UploadAgreementModalProps = {
  isOpen: boolean;
  isLoading: boolean;
  title: string;
  description: string;
  onClose: () => void;
  onSubmit: (file: File) => void;
};

const UploadAgreementModal = ({
  isOpen,
  isLoading,
  title,
  description,
  onClose,
  onSubmit,
}: UploadAgreementModalProps) => {
  const [file, setFile] = useState<File | null>(null);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      width="w-10/12 sm:w-10/12 md:w-7/12 lg:w-4/12"
      closeOnOutsideClick
      dataCy="business-agreement-upload-modal"
      footer={
        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="ghost"
            colorScheme="cyan"
            dataCy="business-agreement-upload-cancel"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            type="button"
            colorScheme="cyan"
            onClick={() => file && onSubmit(file)}
            isLoading={isLoading}
            loadingText="Uploading..."
            disabled={!file}
            dataCy="business-agreement-upload-submit"
          >
            Upload
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {description}
        </p>
        <FilePicker
          name="agreement"
          dataCy="business-agreement-upload"
          label="Agreement (PDF)"
          accept="application/pdf"
          isLoading={false}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const selected = e.target.files?.[0] ?? null;
            if (selected && !isPdfFile(selected)) {
              showErrorMessage('Only PDF files are allowed.');
              e.target.value = '';
              return;
            }
            if (selected && !isWithinLiveAccessMaxSize(selected)) {
              showErrorMessage('File size must be 10MB or less.');
              e.target.value = '';
              return;
            }
            setFile(selected);
          }}
        />
      </div>
    </Modal>
  );
};

export default UploadAgreementModal;
